// @flow

import createMediator from '@core-ui/mediator';
import { withFeatureFlags } from '@core-ui/feature-flags';
import { getHashParameter, updateHashParameter } from '@core-ui/url-helper';
// $FlowFixMe
import { updateFacetsEmitter } from '@cat/events';
import getDepartmentIdFromCustomUrl from './collaborators/get-department-id-from-custom-url';
import productFetcher from './collaborators/fetch-products';
import updateScrollPosition from './collaborators/update-scroll-position';
import buildUrlHash, {
  removeInvalidHashParams,
} from './collaborators/facets-query-builder';
import getUpdatedListOfAppliedFacets, {
  removeAllFacetsFromAppliedArray,
  updateQuickFacetSelections,
} from './collaborators/update-applied-facets';
import { propTypes } from './types';
import { updateFacetData } from './collaborators/update-facet-data';
import { adaptProductModelToggle } from '@cat/size-model-toggle';
// $FlowFixMe
import { facetEngagementDatalayerEvent } from '../../datalayer/events/facet-engagement';
// $FlowFixMe
import { engagementsString } from '../../datalayer/events/facet-engagement/adapters';
import getUpdatedOrderedFacetList from './collaborators/update-ordered-facets';
// $FlowFixMe
import { storageHelper } from '@core-ui/utility';
import departmentMap from './adapters/new-contract/data/departmentMap';

export const Mediator = createMediator('left-faceted-grid', {
  withState: true,
});

export const getOptionalPreviewState = (previewEnabled: boolean) => {
  if (!previewEnabled) {
    return Promise.resolve({});
  }

  return import(
    /* webpackChunkName: 'preview-bar-read' */ 'preview-bar/read.js'
  )
    .then(({ all: previewState }) => previewState())
    .catch((e) => {
      console.error('preview state failed to load', e);
      return {};
    });
};

const getIgnoredFacets = (shouldHideDepartment = false) => {
  const ignoredFacets = ['sortByDir', 'sortByField', 'storeId', 'modelOn'];

  if (shouldHideDepartment) {
    ignoredFacets.push('department');
  }

  return ignoredFacets;
};

type SelectedParam = Array<{ facetName: string, id: string }>;
type ModelToggleData = { mixedGrid: boolean, sizeValue: string };

const getAppliedParam = (param: string): SelectedParam => {
  const value = getHashParameter(param);
  const hasValue = !!value;

  return hasValue ? [{ facetName: param, id: value }] : [];
};

const buildUrlSearchParamFromStyleParams = (urlSearchParam, params) => {
  if (urlSearchParam.length === 0 && params?.style) {
    urlSearchParam = `style=${params.style}`;
  }
  return urlSearchParam;
};

const buildUrlSearchParamFromColorParams = (urlSearchParam, params) => {
  if (urlSearchParam.length === 0 && params?.color?.id) {
    urlSearchParam = `color=${params.color.id}`;
  }
  return urlSearchParam;
};

const buildUrlSearchParamCustomParams = (urlSearchParam, customUrlParams) => {
  if (urlSearchParam.length === 0 && customUrlParams) {
    urlSearchParam = customUrlParams;
  }
  return urlSearchParam;
};

const getCustomUrl = (props) => {
  return props.currentCategory && props.currentCategory.customUrl;
};

const getCustomUrlHash = (customUrl) => {
  return customUrl && customUrl.slice(customUrl.indexOf('#'));
};

const getCustomUrlParams = (customUrlHash) => {
  return customUrlHash && removeInvalidHashParams(customUrlHash);
};

const replaceDepartment = (queryParamsStr, match) => {
  // $FlowFixMe
  const [stringToReplace, departmentId] = match;

  const departmentName = departmentMap[departmentId] || departmentId;
  return queryParamsStr.replace(
    stringToReplace,
    'department=' + departmentName
  );
};

const adaptDepartment = (queryParamsStr, isCatSwitchContractEnabled) => {
  if (!isCatSwitchContractEnabled) return queryParamsStr;
  const match = queryParamsStr.match(/department=([^&]*)/);
  if (!match) return queryParamsStr;
  queryParamsStr = replaceDepartment(queryParamsStr, match);
  return queryParamsStr;
};

/* eslint-disable complexity */
const getShouldUpdateState = ({
  prevProps,
  bopisData: {
    bopisInitialed,
    activedPageType,
    enabled,
    selectedStore,
    active,
  },
  isBRRedesignEnabled,
}) => {
  return (
    bopisInitialed !== prevProps.bopisData.bopisInitialed &&
    enabled &&
    activedPageType === 'category' &&
    selectedStore?.storeId &&
    active &&
    !isBRRedesignEnabled
  );
};
/* eslint-enable complexity */

const getDepartmentId = (isDepartmentAnOption, departmentId) => {
  // We are only going to send the departmentId if department is NOT an option.
  // If we always send it when department is a valid option for the user,
  // then we remove their ability to clear that option as it will always be applied.
  return isDepartmentAnOption === true ? '' : departmentId;
};

const updateSessionStorageScrolledSubCatId = (isFaceting) => {
  // $FlowFixMe
  if (!isFaceting && sessionStorage) {
    const { removeItem } = storageHelper(sessionStorage);
    removeItem('scrolledSubcatId');
  }
};

const getShouldFetchProducts = (facetQueryString, latestQueryString) => {
  return (
    (facetQueryString && facetQueryString === latestQueryString) ||
    !facetQueryString
  );
};

class LeftFacetedGridMediator extends Mediator {
  // $FlowFixMe
  constructor(props) {
    super(props);
    const { abbrBrand, currentCategory, market } = props;
    const customUrl = currentCategory ? currentCategory.customUrl : '';
    const hideDepartmentFacet =
      props.enabledFeatures[`department-facet-hide-${market}-${abbrBrand}`];
    const isRatingsFacetEnabled = props.isRatingsFacetEnabled;

    this.state = {
      ...this.props,
      departmentId: getDepartmentIdFromCustomUrl(customUrl),
      error: '',
      facets: {
        appliedFacets: {},
      },
      orderedFacets: [],
      appliedQuickFacets: [],
      modelToggleData: {
        mixedGrid: false,
        sizeValue: false,
      },
      appliedFacetsCount: 0,
      hideDepartmentFacet,
      ignoredFacets: getIgnoredFacets(hideDepartmentFacet),
      isDepartmentAnOption: props.isDepartmentAnOption || false,
      isRatingsFacetEnabled,
      reportedInitialResponse: false,
      sizeGroups:
        props.featureVariables?.[`cat-model-toggle-${market}`]?.sizeGroups,
    };
    this.bindActions();
    // $FlowFixMe
    this.hashChangeHandler = this.hashChangeHandler.bind(this);
    this.latestQueryString = '';
    this.disablePlaceholders = false;
  }

  componentDidMount() {
    const customUrl = getCustomUrl(this.props);
    const customUrlHash = getCustomUrlHash(customUrl);
    const customUrlParams = getCustomUrlParams(customUrlHash);

    let urlSearchParam = removeInvalidHashParams(window.location.hash);
    urlSearchParam = buildUrlSearchParamFromStyleParams(
      urlSearchParam,
      this.props.params
    );
    urlSearchParam = buildUrlSearchParamFromColorParams(
      urlSearchParam,
      this.props.params
    );
    urlSearchParam = buildUrlSearchParamCustomParams(
      urlSearchParam,
      customUrlParams
    );

    urlSearchParam = adaptDepartment(
      urlSearchParam,
      this.props.enabledFeatures['cat-switch-contract']
    );

    if (
      urlSearchParam.includes('sortByDir') &&
      urlSearchParam.includes('sortByField')
    ) {
      this.setState({
        facets: {
          appliedFacets: {
            sortByField: getAppliedParam('sortByField'),
            sortByDir: getAppliedParam('sortByDir'),
          },
        },
      });
    }

    this.setState({ urlHash: urlSearchParam });
    this.latestQueryString = urlSearchParam;
    this.callFetchProducts(false, this.props.cid, urlSearchParam);
    window.addEventListener('hashchange', this.hashChangeHandler);
  }

  // $FlowFixMe
  componentDidUpdate(prevProps) {
    const { bopisData, isBRRedesignEnabled } = this.props;
    const { selectedStore } = bopisData;

    const shouldUpdateState = getShouldUpdateState({
      prevProps,
      bopisData,
      isBRRedesignEnabled,
    });

    if (shouldUpdateState) {
      this.setState(({ facets }) => ({
        facets: {
          ...facets,
          appliedFacets: {
            ...facets.appliedFacets,
            storeId: [
              {
                isSelected: true,
                facetName: 'storeId',
                facetOption: selectedStore?.storeId,
                searchFacetOptionId: selectedStore?.storeId,
              },
            ],
          },
        },
      }));
      updateHashParameter('storeId', selectedStore?.storeId);
    }
  }

  hashChangeHandler() {
    const { params } = this.props;

    const parameters = window.location.hash.substring(1).split('&');

    const styleParameter = params?.style ? `style=${params.style}` : null;
    const colorParameter = params?.color?.id
      ? `color=${params.color.id}`
      : null;

    let hash = [...parameters, styleParameter, colorParameter]
      .filter(Boolean)
      .join('&');

    hash = adaptDepartment(
      hash,
      this.props.enabledFeatures['cat-switch-contract']
    );
    this.latestQueryString = hash;
    this.callFetchProducts(true, this.props.cid, hash);
  }

  updateProductGroupsBySizeModel(sizeGroup: string) {
    if (!this.state.isModelToggleEnabled) {
      return;
    }

    const productGroups =
      this.state.productGroups &&
      this.state.productGroups.map((group) => ({
        ...group,
        products: group.products.map((product, position) =>
          adaptProductModelToggle({
            sizeGroup,
            product,
            position,
            brand: this.props.abbrBrand,
            sizeGroups: this.state.sizeGroups,
          })
        ),
      }));

    this.setState((prevState) => ({
      ...prevState,
      productGroups,
      sizeGroup,
    }));
  }

  setModelToggleData(modelToggleData: ModelToggleData) {
    this.setState((prevState) => ({
      ...prevState,
      modelToggleData,
    }));
  }

  sendDataToAnalytics(modelToggleData?: ModelToggleData) {
    facetEngagementDatalayerEvent(
      this.props.datalayer,
      this.state.facetQueryString,
      this.props,
      this.state.facets,
      this.props.isDynamicStyleFacetNameEnabled,
      this.state.appliedQuickFacets,
      modelToggleData || this.state.modelToggleData
    );

    if (modelToggleData) {
      this.setModelToggleData(modelToggleData);
    }
  }

  setFacetQueryStringOnStorage() {
    if (window.sessionStorage) {
      const { setItem } = storageHelper(sessionStorage);
      setItem(
        'facetQueryString',
        engagementsString({
          queryString: this.state.facetQueryString,
          facetData: this.state.facets,
          appliedQuickFacets: this.state.appliedQuickFacets,
          modelToggleData: this.state.modelToggleData,
        })
      );
    }
  }

  updateMegaNavHeightScroll(megaNavUIData: {}) {
    if (this.state.wasFaceting) {
      updateScrollPosition(megaNavUIData);
    }
    this.setState({ megaNavUIData, wasFaceting: false });
  }

  // $FlowFixMe
  updateFacets(newFacetSelection, paging = {}) {
    // Update scroll position to kick off megaNavUIData height change
    // since megaNav appears on scroll up
    updateScrollPosition(this.state.megaNavUIData);

    // Get an updated list of selected facets given our new selection
    const updatedFacets = getUpdatedListOfAppliedFacets(
      newFacetSelection,
      this.state.facets
    );

    const updatedAppliedQuickFacets = newFacetSelection?.isQuickFacetAction
      ? updateQuickFacetSelections(
          newFacetSelection,
          this.state.appliedQuickFacets
        )
      : this.state.appliedQuickFacets;

    const updatedOrderedFacets = getUpdatedOrderedFacetList(
      updatedFacets.appliedFacets,
      this.state.orderedFacets
    );
    // Save that new list to state so the results can be immediately propagated downward
    this.setState(
      (prevState) => ({
        facets: {
          ...prevState.facets,
          appliedFacets: updatedFacets.appliedFacets,
        },
        appliedQuickFacets: updatedAppliedQuickFacets,
        orderedFacets: updatedOrderedFacets,
        wasFaceting: true,
      }),
      () => {
        if (newFacetSelection?.facetName === 'sortByField') {
          return this.updateFacets(newFacetSelection?.direction);
        }

        // Build our query string based on our new selected facet list
        const queryString = buildUrlHash(
          {
            ...this.state.facets.appliedFacets,
            pageId: paging.goToPage,
            facetOrder: this.state.orderedFacets,
          },
          this.props.isSwitchContractEnabled
        );

        // Save our query string and call to get new products
        this.latestQueryString = queryString;
        // $FlowFixMe
        history.pushState(null, null, `#${queryString}`);

        updateFacetsEmitter({ queryString });

        const isFaceting = newFacetSelection?.facetName !== 'storeId';

        this.callFetchProducts(isFaceting, this.props.cid, queryString);
      }
    );
  }

  // $FlowFixMe
  goToPage(paging) {
    this.updateFacets(undefined, paging);
  }

  clearAllFacets() {
    const { ignoredFacets, orderedFacets } = this.state;

    // Scroll page to top of Product Grid Region
    updateScrollPosition(this.state.megaNavUIData);

    const updatedFacets = removeAllFacetsFromAppliedArray(
      this.state.facets.appliedFacets,
      ignoredFacets
    );

    this.setState(
      (prevState) => ({
        facets: {
          ...prevState.facets,
          appliedFacets: updatedFacets,
        },
        orderedFacets: [],
      }),
      () => {
        this.updateFacets();
      }
    );
  }

  // $FlowFixMe
  clearFacet(newFacetSelection) {
    this.updateFacets(newFacetSelection);
  }

  // $FlowFixMe
  callFetchProducts(
    isFaceting: boolean,
    cid: number,
    facetQueryString: string,
    pageIndex: number
  ) {
    updateSessionStorageScrolledSubCatId(isFaceting);
    const shouldFetchProducts = getShouldFetchProducts(
      facetQueryString,
      this.latestQueryString
    );
    if (shouldFetchProducts) {
      const { abbrBrand } = this.props;

      // reset placeholders
      this.setState({
        error: '',
        disablePlaceholders: false,
      });

      const ignoreInventory = this.props.enabledFeatures['cat-br-vintage'];
      const departmentId = getDepartmentId(
        this.state.isDepartmentAnOption,
        this.state.departmentId
      );
      const useProductDataWithColorSwatches =
        this.props.enabledFeatures['cat-switch-contract'];

      getOptionalPreviewState(this.props.showPreviewBar)
        .then((previewState) =>
          productFetcher(
            cid,
            departmentId,
            facetQueryString,
            pageIndex,
            this.props.productsUrl,
            this.props.productsApiKey,
            this.state.hideDepartmentFacet,
            abbrBrand === 'hc',
            this.props.abSeg,
            previewState,
            this.props.isFlexFacetEnabled,
            this.props.locale,
            ignoreInventory,
            this.state.isRatingsFacetEnabled,
            this.props.isDynamicStyleFacetNameEnabled,
            this.props.webHierarchy,
            this.props.selectedNodes,
            useProductDataWithColorSwatches,
            this.props.commerceProductsSearchUrl,
            this.props.abbrBrand,
            this.props.market
          )
        )
        .then((data) => {
          this.setState(
            (previousState) =>
              updateFacetData(
                this.props,
                previousState,
                data,
                data.totalItemCount,
                facetQueryString
              ),
            () => {
              if (!this.state.reportedInitialResponse) {
                window.__CATEGORY_PAGE_STATE__.initialProductResponse = data;
                this.setState({
                  reportedInitialResponse: true,
                });
              }
            }
          );
          this.updateProductGroupsBySizeModel(this.state.sizeGroup);
          this.props.onFacetChange(data.facetData);

          if (isFaceting) {
            this.sendDataToAnalytics();
          }

          this.setFacetQueryStringOnStorage();

          return data;
        })
        .catch((err) => {
          const errData = {
            error: err.message,
            productGroups: [],
            disablePlaceholders: true,
          };
          this.setState(() => errData);
          this.state.errorLogger(
            new Error(`[ERROR] Invalid response from product search: ${err}`)
          );
          return errData;
        });
    }
  }

  bindActions() {
    this.actions = {
      sendDataToAnalytics: this.sendDataToAnalytics.bind(this),
      setModelToggleData: this.setModelToggleData.bind(this),
      updateProductGroupsBySizeModel:
        this.updateProductGroupsBySizeModel.bind(this),
      updateMegaNavHeightScroll: this.updateMegaNavHeightScroll.bind(this),
      updateFacets: this.updateFacets.bind(this),
      clearAllFacets: this.clearAllFacets.bind(this),
      clearFacet: this.clearFacet.bind(this),
      goToPage: this.goToPage.bind(this),
    };
  }
}

LeftFacetedGridMediator.propTypes = propTypes;

const FeatureEnabledLeftFacetedGridMediator = withFeatureFlags(
  LeftFacetedGridMediator
);
FeatureEnabledLeftFacetedGridMediator.Colleague =
  LeftFacetedGridMediator.Colleague;

export { LeftFacetedGridMediator as RawLeftFacetedGridMediator };
export default FeatureEnabledLeftFacetedGridMediator;
